import { Injectable } from '@angular/core';
import { Template } from 'apps/prompt-builder-FE/src/core/interface/template.interface';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { RemoteTemplateService } from './remote-template.service';
import { tap, catchError, map } from 'rxjs/operators';
import { GenericResponse } from '@core/interface';

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private templatesSubject = new BehaviorSubject<Template[]>([]);
  templates$ = this.templatesSubject.asObservable();

  private activeId: string | null = null;

  constructor(private remoteTemplateService: RemoteTemplateService) {
    // Removed: this.loadTemplates();
  }

  // New public method to explicitly fetch templates from the backend
  fetchTemplates(): void {
    this.remoteTemplateService.getAll().subscribe(response => {
      if(response.success) this.templatesSubject.next(response.data);
    });
  }

  loadTemplates(): void { // This method is now private and called by other methods
    this.remoteTemplateService.getAll().subscribe(response => {
      if(response.success) this.templatesSubject.next(response.data);
    });
  }

  getAll(): Observable<Template[]> {
    return this.templates$;
  }

  getById(id: string): Observable<Template> {
    return this.remoteTemplateService.getById(id).pipe(map(response => response.data));
  }

  add(template: Partial<Template>): Observable<Template> {
    return this.remoteTemplateService.create(template).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after add
      map(response => response.data),
      catchError(err => {
        console.error('Error creating template', err);
        return throwError(err);
      })
    );
  }

  update(id: string, patch: Partial<Template>): Observable<Template> {
    return this.remoteTemplateService.update(id, patch).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after update
      map(response => response.data),
      catchError(err => {
        console.error('Error updating template', err);
        return throwError(err);
      })
    );
  }

  delete(id: string): Observable<void> {
    return this.remoteTemplateService.delete(id).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after delete
      map(response => response.data),
      catchError(err => {
        console.error('Error deleting template', err);
        return throwError(err);
      })
    );
  }

  duplicate(id: string): Observable<Template> {
    return this.remoteTemplateService.duplicate(id).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after duplicate
      map(response => response.data),
      catchError(err => {
        console.error('Error duplicating template', err);
        return throwError(err);
      })
    );
  }

  setActive(id: string | null): void {
    this.activeId = id;
  }

  getActive(): Template | null {
    if (!this.activeId) return null;
    const templates = this.templatesSubject.getValue();
    return templates.find(t => t.id === this.activeId) || null;
  }

  extractVars(text: string): string[] {
    // More robust regex to catch all variations
    const patterns = [
      /{{\s*([\w.-]+)\s*}}/g,  // {{ variable }}
      /{{([\w.-]+)}}/g,        // {{variable}}
      /{{\s*([\w.-]+)}}/g,     // {{ variable}}
      /{{([\w.-]+)\s*}}/g      // {{variable }}
    ];
    
    const set = new Set<string>();
    
    for (const pattern of patterns) {
      let m;
      while ((m = pattern.exec(text))) {
        set.add(m[1]);
      }
    }
    
    const result = [...set];
    return result;
  }

  getPublicTemplates(page: number, limit: number): Observable<GenericResponse<{ templates: Template[], total: number }>> {
    return this.remoteTemplateService.getPublicTemplates(page, limit);
  }

  searchPublicTemplates(query: string, page : number , limit: number): Observable<GenericResponse<{ templates: Template[], total: number }>> {
    return this.remoteTemplateService.searchPublicTemplates(query, page , limit);
  }

  makePublic(id: string): Observable<Template> {
    return this.remoteTemplateService.makePublic(id).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after makePublic
      map(response => response.data),
      catchError(err => {
        console.error('Error making template public', err);
        return throwError(err);
      })
    );
  }

  clone(id: string): Observable<Template> {
    return this.remoteTemplateService.clone(id).pipe(
      tap(() => this.fetchTemplates()), // Call fetchTemplates after clone
      map(response => response.data),
      catchError(err => {
        console.error('Error cloning template', err);
        return throwError(err);
      })
    );
  }
}


// import { Injectable } from '@angular/core';
// import { Template } from 'apps/prompt-builder-FE/src/core/interface/template.interface';

// @Injectable({
//   providedIn: 'root'
// })
// export class TemplateService {
//   private readonly STORAGE_KEY = 'promptBuilder.templates.v1';

//   private templates: Template[] = [];
//   private activeId: string | null = null;

//   constructor() {
//     this.load();
//     // Seed with a demo template if first run
//     if (this.templates.length === 0) {
//       this.add({
//         id: this.uid(),
//         name: 'Bug Report',
//         desc: 'Structured software bug report',
//         body: `Title: {{title}}
// Product/Area: {{area}}
// Severity: {{severity}}
// Environment: {{env}}

// Steps to Reproduce:
// 1) {{step1}}
// 2) {{step2}}

// Expected:
// {{expected}}

// Actual:
// {{actual}}

// Notes:
// {{notes}}`,
//         createdAt: Date.now(),
//         updatedAt: Date.now()
//       });
//     }
//   }

//   /*** CRUD METHODS ***/

//   getAll(): Template[] {
//     return [...this.templates].sort((a, b) => a.name.localeCompare(b.name));
//   }

//   getById(id: string): Template | undefined {
//     return this.templates.find(t => t.id === id);
//   }

//   add(template: Template): void {
//     this.templates.push(template);
//     this.persist();
//   }

//   update(id: string, patch: Partial<Template>): void {
//     const t = this.getById(id);
//     if (!t) return;
//     Object.assign(t, patch, { updatedAt: Date.now() });
//     this.persist();
//   }

//   duplicate(id: string): Template | null {
//     const t = this.getById(id);
//     if (!t) return null;
//     const copy: Template = {
//       ...t,
//       id: this.uid(),
//       name: `${t.name} (copy)`,
//       createdAt: Date.now(),
//       updatedAt: Date.now()
//     };
//     this.add(copy);
//     return copy;
//   }

//   delete(id: string): void {
//     this.templates = this.templates.filter(t => t.id !== id);
//     if (this.activeId === id) {
//       this.activeId = null;
//     }
//     this.persist();
//   }

//   /*** ACTIVE TEMPLATE ***/

//   setActive(id: string | null): void {
//     this.activeId = id;
//   }

//   getActive(): Template | null {
//     return this.activeId ? this.getById(this.activeId) ?? null : null;
//   }

//   /*** STORAGE HELPERS ***/

//   private persist(): void {
//     localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.templates));
//   }

//   private load(): void {
//     try {
//       this.templates = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
//     } catch {
//       this.templates = [];
//     }
//   }

//   private uid(): string {
//     return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
//   }

//   /*** UTIL: Extract {{variables}} from template body ***/
//   extractVars(text: string): string[] {
//     const re = /{{\s*([\w.-]+)\s*}}/g;
//     const set = new Set<string>();
//     let match;
//     while ((match = re.exec(text))) {
//       set.add(match[1]);
//     }
//     return [...set];
//   }
// }
